import * as React from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { CmdLoader } from '@commander-services/gui-components';
import { IDetailWaypointResponseData } from './types';
import { createInfoWindow as createWaypointInfoWindow, getWaypointDetail } from './WaypointService';
import {
  computedAreaAtom,
  detailWaypointAtom,
  isDrawWaypointAtom,
  isEditModeAtom,
  isWaypointsOpenAtom,
  waypointIdAtom,
  waypointsAtom,
} from './WaypointState';
import { autofocusAtom } from '../OnlineMapState';

export interface IDrawedWaypointProps {
  map: google.maps.Map;
}

interface IWaypointPolygon {
  [waypointId: number]: google.maps.Polygon;
}

export interface IInfoChipsOverlays {
  [waypointId: number]: google.maps.OverlayView;
}

function DrawedWaypoint(props: IDrawedWaypointProps): JSX.Element {
  const waypoints = useRecoilValue(waypointsAtom);
  const [isWaypointsOpen] = useRecoilState(isWaypointsOpenAtom);
  const autofocus = useRecoilValue<boolean>(autofocusAtom);
  // const [filteredWaypointsString] = useRecoilState<string>(WaypointState.filteredWaypointsString);
  // const [waypointsLoaded] = useRecoilState<boolean>(WaypointState.waypointsLoaded);
  // const [filteredWaypoints] = useRecoilState<IWaypoint[]>(WaypointState.filteredWaypoints);
  const [detailWaypoint, setDetailWaypoint] = useRecoilState<IDetailWaypointResponseData | null>(
    detailWaypointAtom
  );
  const [computedArea, setComputedArea] = useRecoilState<number>(computedAreaAtom);
  // const [removedWaypointId, setRemovedWaypointId] = useRecoilState<number | null>(
  //   WaypointState.removedWaypointId
  // );
  const [loadDetail, setLoadDetail] = React.useState<boolean>(false);
  const isEditMode = useRecoilValue<boolean>(isEditModeAtom);
  const isDrawWaypoint = useRecoilValue<boolean>(isDrawWaypointAtom);
  const [waypointId, setWaypointId] = useRecoilState<number | null>(waypointIdAtom);

  const { current: waypointPolygons } = React.useRef<IWaypointPolygon>({});
  const { current: infoAreaOverlay } = React.useRef<IInfoChipsOverlays>({});

  const createInfoWindow = React.useCallback(
    (position: google.maps.LatLng, waypointID: number) => {
      const areaInfo = createWaypointInfoWindow(position, waypointPolygons[waypointID]);
      infoAreaOverlay[waypointID] = areaInfo.newWindow;
      setComputedArea(areaInfo.area);
      infoAreaOverlay[waypointID].setMap(props.map);
    },
    [infoAreaOverlay, props.map, setComputedArea, waypointPolygons]
  );

  const removeItemsFromMap = React.useCallback(() => {
    const tempWaypointPolygon = waypointPolygons;

    Object.keys(tempWaypointPolygon).forEach((waypointToRemove) => {
      waypointPolygons[waypointToRemove].setMap(null);
      delete waypointPolygons[waypointToRemove];
    });

    const tempArea = infoAreaOverlay;
    Object.keys(tempArea).forEach((waypointToRemove) => {
      // if (
      //   typeof waypointId === 'boolean' ||
      //   Number(waypointToRemove) !== waypointId ||
      //   !isWaypointsOpen ||
      //   isEditMode ||
      //   isDrawWaypoint
      // ) {
      infoAreaOverlay[waypointToRemove].setMap(null);
      delete infoAreaOverlay[waypointToRemove];
      // }
    });
  }, [infoAreaOverlay, waypointPolygons]);

  const handleClickWaypointMarker = React.useCallback(async () => {
    if (typeof waypointId === 'number') {
      setLoadDetail(false);

      const selectedWaypoint = await getWaypointDetail(waypointId);

      if (selectedWaypoint) {
        setDetailWaypoint(selectedWaypoint);
        setLoadDetail(true);
      } else {
        setLoadDetail(true);

        setWaypointId(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waypoints, waypointId]);

  React.useEffect(() => {
    handleClickWaypointMarker();
  }, [handleClickWaypointMarker]);

  React.useEffect(() => {
    if (
      props.map &&
      detailWaypoint &&
      waypointId &&
      // waypointsLoaded &&
      !isDrawWaypoint &&
      !isEditMode
    ) {
      removeItemsFromMap();

      if (detailWaypoint && detailWaypoint.coordinates && detailWaypoint.centerPoint) {
        const polygonPath: any[] = [];

        detailWaypoint.coordinates.forEach((waypointPosition) => {
          polygonPath.push({
            lat: waypointPosition.latitude,
            lng: waypointPosition.longitude,
          });
        });

        if (!waypointPolygons[waypointId]) {
          const newPolygon = new google.maps.Polygon({
            paths: polygonPath,
            strokeColor: '#B50536',
            strokeWeight: 3,
            fillColor: '#FCD4DF',
            fillOpacity: 0.5,
            clickable: false,
          });

          waypointPolygons[waypointId] = newPolygon;

          waypointPolygons[waypointId].setMap(props.map);
          if (isWaypointsOpen) {
            const bounds = new google.maps.LatLngBounds();
            polygonPath.forEach((path) => {
              bounds.extend(path);
            });

            props.map.fitBounds(bounds, { top: 50, right: 50, bottom: 100, left: 200 });
          }
        }

        if (!infoAreaOverlay[waypointId] && waypointPolygons[waypointId] && isWaypointsOpen) {
          const bounds = new google.maps.LatLngBounds();
          polygonPath.forEach((path) => {
            bounds.extend(path);
          });

          const center: google.maps.LatLng = new google.maps.LatLng(
            detailWaypoint.centerPoint.latitude,
            detailWaypoint.centerPoint.longitude
          );

          createInfoWindow(center, waypointId);
        }

        if (waypointPolygons[waypointId] && Object.keys(waypointPolygons).length > 1) {
          Object.keys(waypointPolygons).forEach((waypointToRemove) => {
            if (Number(waypointToRemove) !== waypointId) {
              waypointPolygons[waypointToRemove].setMap(null);
              delete waypointPolygons[waypointToRemove];
            }
          });
          if (autofocus && typeof waypointId === 'number') {
            const bounds = new google.maps.LatLngBounds();
            polygonPath.forEach((path) => {
              bounds.extend(path);
            });

            props.map.fitBounds(bounds, { top: 50, right: 50, bottom: 100, left: 200 });
          }
        }
      }
    }

    if (!isWaypointsOpen || isEditMode || isDrawWaypoint || !waypointId) {
      removeItemsFromMap();
    }

    return () => {
      removeItemsFromMap();
    };
  }, [
    props.map,
    detailWaypoint,
    waypointId,
    // waypointsLoaded,
    isDrawWaypoint,
    isEditMode,
    isWaypointsOpen,
    removeItemsFromMap,
    waypointPolygons,
    infoAreaOverlay,
    createInfoWindow,
    autofocus,
  ]);

  // React.useEffect(() => {
  //   if (Object.keys(infoAreaOverlay).length > 0) {
  //     const tempArea = infoAreaOverlay;
  //     Object.keys(tempArea).map((waypointToRemove) => {
  //       if (
  //         typeof waypointId === 'boolean' ||
  //         Number(waypointToRemove) !== waypointId ||
  //         !isWaypointsOpen ||
  //         isEditMode ||
  //         isDrawWaypoint
  //       ) {
  //         infoAreaOverlay[waypointToRemove].setMap(null);
  //         delete infoAreaOverlay[waypointToRemove];
  //       }
  //     });
  //   }
  // }, [infoAreaOverlay, waypointId, isWaypointsOpen, isEditMode, isDrawWaypoint]);

  // React.useEffect(() => {
  //   if (typeof waypointId === 'number' && waypointPolygons[waypointId]) {
  //     google.maps.event.addListenerOnce(waypointPolygons[waypointId], 'mouseover', () => {
  //       const area = google.maps.geometry.spherical.computeArea(
  //         waypointPolygons[waypointId].getPath()
  //       );
  //       const formatArea = (area / 1000000).toFixed(2);
  //     });
  //   }
  // });

  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{!loadDetail && typeof waypointId === 'number' && <CmdLoader inContent={true} />}</>;
}

export default DrawedWaypoint;
